/// <reference path="../mapbox-polyline.d.ts" />
import polyline from "@mapbox/polyline"
import { publicFetch } from "./client"
import type { Location, RouteOption, RouteStep } from "./types"

const TRANSIT_COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#8B5CF6", "#EF4444"]
const WALK_COLOR = "#D69E2E"

interface ItineraryLeg {
  mode: string
  duration?: number
  distance?: number
  from?: { name?: string }
  from_stop_id?: string
  to?: { name?: string }
  to_stop_id?: string
  legGeometry?: { points: string }
  route?: { shortName?: string; id?: string; longName?: string }
  stops?: string[]
  duration_p10?: number
  duration_p50?: number
  duration_p90?: number
  confidence?: number
  scheduled?: boolean
  headway_min_secs?: number
  headway_max_secs?: number
}

interface Itinerary {
  duration?: number
  walkDistance?: number
  legs?: ItineraryLeg[]
  total_p10?: number
  total_p50?: number
  total_p90?: number
  num_transfers?: number
  confidence?: number
  fare?: number
}

type PlanRouteOptions = {
  citySlug?: string
}

export async function planRoute(
  origin: Location,
  destination: Location,
  options?: PlanRouteOptions,
): Promise<RouteOption[]> {
  try {
    const citySlug = options?.citySlug?.trim()
    const data = await publicFetch<{ plan?: { itineraries?: Itinerary[] } }>("/routes/plan", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        from_lat: origin.latitude.toString(),
        from_lon: origin.longitude.toString(),
        to_lat: destination.latitude.toString(),
        to_lon: destination.longitude.toString(),
        ...(citySlug ? { city_slug: citySlug } : {}),
      }),
    })

    if (!data.plan?.itineraries || !Array.isArray(data.plan.itineraries)) return []

    return data.plan.itineraries.map((itinerary, index) => {
      const legs = itinerary.legs || []
      const allCoordinates: number[][] = []
      let trotroCount = 0
      const busLegs = legs.filter((leg) => leg.mode === "BUS").length
      const itineraryFare =
        typeof itinerary.fare === "number" && itinerary.fare > 0
          ? itinerary.fare
          : undefined
      const perLegFare =
        itineraryFare !== undefined && busLegs > 0
          ? itineraryFare / busLegs
          : undefined

      const steps: RouteStep[] = legs.map((leg, legIndex) => {
        let legCoords: number[][] = []
        if (leg.legGeometry?.points) {
          legCoords = polyline
            .decode(leg.legGeometry.points)
            .map(([lat, lng]: [number, number]) => [lng, lat])
          allCoordinates.push(...legCoords)
        }

        const isTrotro = leg.mode !== "WALK"
        let stepColor: string
        let stepRouteIndex: number | undefined

        if (isTrotro) {
          stepColor = TRANSIT_COLORS[trotroCount % TRANSIT_COLORS.length]
          stepRouteIndex = trotroCount
          trotroCount++
        } else {
          stepColor = WALK_COLOR
        }

        return {
          id: `step-${index}-${legIndex}`,
          type: leg.mode === "WALK" ? "walking" : "trotro",
          instruction: `${leg.mode === "WALK" ? "Walk" : "Take trotro"} from ${leg.from?.name || "Start"} to ${leg.to?.name || "End"}`,
          duration: leg.duration || 0,
          distance: leg.distance || 0,
          from: leg.from?.name,
          fromStopId: leg.from_stop_id,
          to: leg.to?.name,
          toStopId: leg.to_stop_id,
          coordinates: legCoords,
          routeNumber: leg.route?.shortName,
          routeId: leg.route?.id,
          routeName: leg.route?.longName,
          stops: leg.stops,
          fare: leg.mode === "BUS" ? perLegFare : undefined,
          color: stepColor,
          routeIndex: stepRouteIndex,
          scheduled: isTrotro ? leg.scheduled : undefined,
          durationP10: leg.duration_p10,
          durationP90: leg.duration_p90,
          confidence: leg.confidence,
          headwayMinSecs: leg.headway_min_secs,
          headwayMaxSecs: leg.headway_max_secs,
        } satisfies RouteStep
      })

      const transfers = Math.max(0, busLegs - 1)
      const totalDistance = legs.reduce((sum, leg) => sum + (leg.distance || 0), 0) || itinerary.walkDistance || 0

      return {
        id: `route-${index}`,
        type: index === 0 ? "recommended" : index === 1 ? "fastest" : index === 2 ? "shortest" : "cheapest",
        title: busLegs > 0 ? `Trotro Route ${index + 1}` : "Walking Only",
        totalTime: itinerary.duration || 0,
        totalDistance,
        totalPrice: busLegs > 0 ? (itineraryFare ?? 0) : 0,
        transfers,
        steps,
        coordinates: allCoordinates,
        totalP10: itinerary.total_p10,
        totalP90: itinerary.total_p90,
        confidence: itinerary.confidence,
      } satisfies RouteOption
    })
  } catch (error) {
    console.error("Route planning error:", error)
    throw error
  }
}
